import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { HistoryModel } from '../../types/models';
import { formatChatDate } from './CMShared';

interface ChatListItemProps {
	chat: HistoryModel;
	isSelected: boolean;
	isActive: boolean;
	isCurrentChat: boolean;
	onSelect: () => void;
	onToggle: () => void;
	onOpen: () => void;
	onExport: () => void;
	onDelete: () => void;
	/** Inline title edit commit — calls renameChat API directly. */
	onRename: (chatId: string, newTitle: string) => Promise<void>;
	/** Rename button — opens the rename modal. */
	onOpenRenameModal: (chatId: string, title: string) => void;
}

export const ChatListItem = memo(function ChatListItem({
	chat,
	isSelected,
	isActive,
	isCurrentChat,
	onSelect,
	onToggle,
	onOpen,
	onExport,
	onDelete,
	onRename,
	onOpenRenameModal,
}: ChatListItemProps) {
	const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [localTitle, setLocalTitle] = useState<string>(chat.title);
	const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
	const inputRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		if (isEditingTitle && inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, [isEditingTitle]);

	useEffect(() => {
		setLocalTitle(chat.title);
	}, [chat.title]);

	useEffect(() => {
		return () => {
			if (clickTimeoutRef.current) {
				clearTimeout(clickTimeoutRef.current);
			}
		};
	}, []);

	const commitTitleEdit = useCallback(async () => {
		const trimmedTitle = localTitle.trim();
		setIsEditingTitle(false);
		if (!trimmedTitle || trimmedTitle === chat.title.trim()) {
			setLocalTitle(chat.title);
			return;
		}
		await onRename(chat.id, trimmedTitle);
	}, [localTitle, chat.id, chat.title, onRename]);

	const cancelTitleEdit = useCallback(() => {
		setIsEditingTitle(false);
		setLocalTitle(chat.title);
	}, [chat.title]);

	const handleChatClick = useCallback(() => {
		if (clickTimeoutRef.current) {
			clearTimeout(clickTimeoutRef.current);
			clickTimeoutRef.current = null;
		}
		clickTimeoutRef.current = setTimeout(() => {
			onSelect();
			clickTimeoutRef.current = null;
		}, 200);
	}, [onSelect]);

	const handleTitleDoubleClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			if (clickTimeoutRef.current) {
				clearTimeout(clickTimeoutRef.current);
				clickTimeoutRef.current = null;
			}
			onSelect();
			setIsEditingTitle(true);
		},
		[onSelect],
	);

	const handleRenameButtonClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onOpenRenameModal(chat.id, chat.title);
		},
		[chat.id, chat.title, onOpenRenameModal],
	);

	let itemBg = 'bg-neutral-500 border-neutral-400 hover:bg-neutral-400';
	if (isSelected) itemBg = 'bg-red-600/20 border-red-500/50';
	else if (isActive) itemBg = 'bg-neutral-400/50 border-neutral-300';

	return (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
		<div
			className={`flex items-center p-sm rounded-small cursor-pointer transition-colors duration-200 border ${itemBg} ${isCurrentChat ? 'ring-1 ring-red-400/40' : ''}`}
			onClick={handleChatClick}
		>
			<div className="mr-sm flex-shrink-0">
				<input
					type="checkbox"
					checked={isSelected}
					onChange={(e) => {
						e.stopPropagation();
						onToggle();
					}}
					onClick={(e) => e.stopPropagation()}
					className="accent-red-600 w-5 h-5 cursor-pointer"
					aria-label={`Select chat: ${chat.title}`}
				/>
			</div>

			<div className="flex-1 min-w-0">
				{isEditingTitle ? (
					<input
						ref={inputRef}
						value={localTitle}
						onChange={(e) => setLocalTitle(e.target.value)}
						onClick={(e) => e.stopPropagation()}
						onBlur={commitTitleEdit}
						onKeyDown={(e) => {
							if (e.key === 'Enter') commitTitleEdit();
							if (e.key === 'Escape') cancelTitleEdit();
						}}
						className="w-full bg-neutral-600 border border-neutral-400 rounded-small px-xs py-xxs text-sm text-neutral-100 outline-none"
						aria-label="Edit chat title"
					/>
				) : (
					<h4
						className="text-sm font-medium text-neutral-100 truncate cursor-text"
						onDoubleClick={handleTitleDoubleClick}
						title="Double-click to edit title"
					>
						{isCurrentChat && (
							<span className="text-xs text-red-400 font-normal">
								(Active){' '}
							</span>
						)}
						{localTitle}
					</h4>
				)}
				<p className="text-xs text-neutral-300 truncate">
					{formatChatDate(chat.created_at)}
				</p>
			</div>

			<div className="flex items-center gap-xs ml-sm flex-shrink-0">
				<button
					onClick={handleRenameButtonClick}
					className="btn-icon text-neutral-300 hover:text-neutral-100 hover:bg-neutral-600"
					aria-label="Rename chat"
					title="Rename chat"
					type="button"
				>
					<i className="fas fa-pencil-alt text-xs" />
				</button>
				<button
					onClick={(e) => {
						e.stopPropagation();
						onOpen();
					}}
					className="btn-icon text-neutral-300 hover:text-neutral-100 hover:bg-neutral-600"
					aria-label="Open chat"
					title="Open chat"
					type="button"
				>
					<i className="fas fa-external-link-alt text-xs" />
				</button>
				<button
					onClick={(e) => {
						e.stopPropagation();
						onExport();
					}}
					className="btn-icon text-neutral-300 hover:text-red-400 hover:bg-neutral-600"
					aria-label="Export chat"
					title="Export chat"
					type="button"
				>
					<i className="fas fa-download text-xs" />
				</button>
				<button
					onClick={(e) => {
						e.stopPropagation();
						onDelete();
					}}
					className="btn-icon text-neutral-300 hover:text-red-500 hover:bg-neutral-600"
					aria-label="Delete chat"
					title="Delete chat"
					type="button"
				>
					<i className="fas fa-trash text-xs" />
				</button>
			</div>
		</div>
	);
});
